import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cn } from '@/lib/cn';

// Three variants. Primary = solid indigo CTA. Secondary = tinted indigo for
// less-important actions. Ghost = no background, for icon-only toolbar buttons
// and similar. Use these instead of ad-hoc class soup.

type Variant = 'primary' | 'secondary' | 'ghost';
type Size = 'sm' | 'md';

type Props = ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: Variant;
  size?: Size;
};

const VARIANT_CLASSES: Record<Variant, string> = {
  primary:
    'bg-indigo-500 text-white hover:bg-indigo-400 active:bg-indigo-600 disabled:bg-zinc-800 disabled:text-zinc-400',
  secondary:
    'bg-indigo-500/15 border border-indigo-500/40 text-indigo-200 hover:bg-indigo-500/25 active:bg-indigo-500/30 disabled:opacity-50',
  ghost:
    'text-zinc-300 hover:bg-zinc-900 hover:text-zinc-100 disabled:opacity-50',
};

const SIZE_CLASSES: Record<Size, string> = {
  sm: 'text-xs px-2 py-1.5 rounded',
  md: 'text-sm px-3 py-1.5 rounded-md',
};

export const Button = forwardRef<HTMLButtonElement, Props>(function Button(
  { variant = 'secondary', size = 'md', className, type = 'button', ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      className={cn(
        'inline-flex items-center gap-2 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 focus-visible:ring-offset-zinc-950',
        SIZE_CLASSES[size],
        VARIANT_CLASSES[variant],
        className,
      )}
      {...rest}
    />
  );
});
